import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './BillReceipt.css';

const EMPTY = { receipt_date: today(), customer_id: '', party_name: '', bill_no: '', bill_amt: '', received: '', mode: 'Cash', bank_id: '' };

export default function BillReceipt() {
  const { data: customers } = useApi(() => api.customers.creditList(), []);
  const { data: records, loading, error, reload } = useApi(() => api.billReceipts.list(), []);
  const { data: bankData, loading: banksLoading } = useApi(() => api.banks.list(), [], { banks: [] });
  const banks = bankData?.banks || [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const isToday = (d) => d === today();

  const { user, can, canAccess } = useAuth();
  const isAdmin = can('bill_receipts', 'approve');

  // UX Filtering: Filter banks based on selected mode
  const filteredBanks = React.useMemo(() => {
    const cash = banks.filter(b => b.account_type === 'Cash' || b.name.toLowerCase().includes('cash'));
    const nonCash = banks.filter(b => b.account_type !== 'Cash' && !b.name.toLowerCase().includes('cash'));
    if (form.mode === 'Cash') return cash.length > 0 ? cash : banks;
    return nonCash.length > 0 ? nonCash : banks;
  }, [banks, form.mode]);

  const editFilteredBanks = React.useMemo(() => {
    if (!editRow) return [];
    const cash = banks.filter(b => b.account_type === 'Cash' || b.name.toLowerCase().includes('cash'));
    const nonCash = banks.filter(b => b.account_type !== 'Cash' && !b.name.toLowerCase().includes('cash'));
    if (editRow.mode === 'Cash') return cash.length > 0 ? cash : banks;
    return nonCash.length > 0 ? nonCash : banks;
  }, [banks, editRow?.mode]);

  // Auto-select default bank if available, otherwise stay on "-- Select Account --"
  React.useEffect(() => {
    if (showForm && filteredBanks.length > 0) {
      const defaultBank = filteredBanks.find(b => Number(b.is_default) === 1);
      const currentValid = filteredBanks.find(b => b.id === Number(form.bank_id));
      if (!currentValid) {
        setForm(prev => ({ ...prev, bank_id: defaultBank ? defaultBank.id : '' }));
      }
    }
  }, [form.mode, filteredBanks, showForm]);

  const handleSave = async () => {
    if (!form.party_name || !form.received) { notify('❌ Party and Amount required'); return; }
    if (!form.bank_id) { notify('❌ Please select a Bank/Account'); return; }
    setSaving(true);
    try {
      await api.billReceipts.create({
        receipt_date: form.receipt_date,
        customer_id: form.customer_id || null,
        party_name: form.party_name,
        bill_no: form.bill_no,
        bill_amt: parseFloat(form.bill_amt) || 0,
        received: parseFloat(form.received),
        mode: form.mode,
        bank_id: parseInt(form.bank_id),
      });
      notify('✅ Bill receipt saved'); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    if (!editRow.party_name || !editRow.received) { notify('❌ Party and Amount required'); return; }
    if (!editRow.bank_id) { notify('❌ Please select a Bank/Account'); return; }
    setSaving(true);
    try {
      await api.billReceipts.update(editRow.id, {
        party_name: editRow.party_name,
        received: parseFloat(editRow.received),
        mode: editRow.mode,
        bank_id: parseInt(editRow.bank_id)
      });
      notify('✅ Bill receipt updated'); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try { await api.billReceipts.remove(confirm.row.id); notify('✅ Deleted'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const totalReceived = (records || []).reduce((s, r) => s + Number(r.received || 0), 0);
  if (!canAccess('bill_receipts')) {
    return (
      <div className="bill-receipt-page">
        <ErrBanner msg="Access Denied: You do not have permission to access bill receipts." />
      </div>
    );
  }
  const handleExport = () => {
    const printWindow = window.open('', '_blank');
    const todayStr = new Date().toLocaleDateString();

    let tableHtml = `
      <html>
        <head>
          <title>Bill Receipt Report - ${todayStr}</title>
          <style>
            body { font-family: 'Nunito', sans-serif; padding: 20px; color: #333; }
            h2 { color: #1a6fad; margin-bottom: 5px; }
            .meta { font-size: 12px; color: #666; margin-bottom: 20px; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background: #f8fafc; color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px; border: 1px solid #e2e8f0; text-align: left; }
            td { padding: 10px; border: 1px solid #e2e8f0; font-size: 12px; }
            .text-right { text-align: right; }
            .total-row { background: #f1f5f9; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Bill Receipt Report</h2>
          <div class="meta">Generated on: ${todayStr} | Total Receipts: ₹${fmtNum(totalReceived)}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Receipt No</th>
                <th>Party</th>
                <th>Account</th>
                <th>Bill No</th>
                <th class="text-right">Bill Amt (₹)</th>
                <th class="text-right">Received (₹)</th>
                <th class="text-right">Balance (₹)</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              ${records.map((r, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${r.receipt_date}</td>
                  <td style="font-weight: bold; color: #1a6fad;">${r.receipt_no}</td>
                  <td>${r.party_name}</td>
                  <td>${r.bank_name || 'Cash'}</td>
                  <td>${r.bill_no}</td>
                  <td class="text-right">₹${fmtNum(r.bill_amt || 0)}</td>
                  <td class="text-right">₹${fmtNum(r.received)}</td>
                  <td class="text-right">₹${fmtNum(Number(r.bill_amt || 0) - Number(r.received || 0))}</td>
                  <td>${r.mode}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="7" class="text-right">TOTAL RECEIVED</td>
                <td class="text-right">₹${fmtNum(totalReceived)}</td>
                <td colspan="2"></td>
              </tr>
            </tbody>
          </table>
          <script>window.print();</script>
        </body>
      </html>
    `;

    printWindow.document.write(tableHtml);
    printWindow.document.close();
  };


  return (

    <div className="bill-receipt-page">
      <div className="page-header">
        <h2 className="page-title">Bill Receipt</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {can('bill_receipts', 'create') && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
              ＋ New Receipt
            </button>
          )}
          <button className="export-btn" onClick={handleExport}>⬆ Export</button>
        </div>
      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      {showForm && (
        <div className="section-card">
          <div className="section-header">New Bill Receipt</div>
          <div className="form-row">
            <div className="form-group"><label>Date</label>
              <input type="date" className="form-control" value={form.receipt_date} onChange={e => setForm({ ...form, receipt_date: e.target.value })} />
            </div>
            <div className="form-group"><label>Party</label>
              <select className="form-control" value={form.customer_id} onChange={e => {
                const c = (customers || []).find(x => String(x.id) === e.target.value);
                setForm({ ...form, customer_id: e.target.value, party_name: c ? c.name : '' });
              }}>
                <option value="">-- Select --</option>
                {(customers || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Bill No</label>
              <input className="form-control" value={form.bill_no} onChange={e => setForm({ ...form, bill_no: e.target.value })} placeholder="SL-XXXX" />
            </div>
            <div className="form-group"><label>Bill Amount (₹)</label>
              <input type="number" className="form-control" value={form.bill_amt} onChange={e => setForm({ ...form, bill_amt: e.target.value })} />
            </div>
            <div className="form-group"><label>Amount Received (₹)</label>
              <input type="number" className="form-control" value={form.received} onChange={e => setForm({ ...form, received: e.target.value })} />
            </div>
            <div className="form-group"><label>Mode</label>
              <select className="form-control" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                <option>Cash</option><option>UPI</option><option>NEFT</option><option>RTGS</option><option>Cheque</option>
              </select>
            </div>
            <div className="form-group"><label>Account / Bank</label>
              <select className="form-control" value={form.bank_id} onChange={e => setForm({ ...form, bank_id: e.target.value })}>
                <option value="">{banksLoading ? '-- Loading Accounts --' : '-- Select Account --'}</option>
                {filteredBanks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save Receipt'}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Bill Receipt — {editRow.receipt_no} — {fmtDate(editRow.receipt_date)}</div>
          <div className="form-row">
            <div className="form-group"><label>Party</label>
              <input className="form-control" value={editRow.party_name} onChange={e => setEditRow({ ...editRow, party_name: e.target.value })} disabled={!!editRow.customer_id} />
            </div>
            <div className="form-group"><label>Amount Received (₹)</label>
              <input type="number" className="form-control" value={editRow.received} onChange={e => setEditRow({ ...editRow, received: e.target.value })} />
            </div>
            <div className="form-group"><label>Mode</label>
              <select className="form-control" value={editRow.mode} onChange={e => setEditRow({ ...editRow, mode: e.target.value })}>
                <option>Cash</option><option>UPI</option><option>NEFT</option><option>RTGS</option><option>Cheque</option>
              </select>
            </div>
            <div className="form-group"><label>Account / Bank</label>
              <select className="form-control" value={editRow.bank_id} onChange={e => setEditRow({ ...editRow, bank_id: e.target.value })}>
                <option value="">{banksLoading ? '-- Loading Accounts --' : '-- Select Account --'}</option>
                {editFilteredBanks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving…' : '💾 Update Receipt'}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}

      {!loading && (
        <div className="section-card">
          <div className="section-header">Bill Receipts — Total: ₹{fmtNum(totalReceived)}</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Receipt No</th><th style={{ textAlign: 'left' }}>Party</th>
                <th style={{ textAlign: 'left' }}>Account</th>
                <th style={{ textAlign: 'left' }}>Bill No</th><th style={{ textAlign: 'right' }}>Bill Amt (₹)</th>
                <th style={{ textAlign: 'right' }}>Received (₹)</th><th style={{ textAlign: 'right' }}>Balance (₹)</th><th style={{ textAlign: 'left' }}>Mode</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(records || []).map((r, i) => {
                  const balance = Number(r.bill_amt || 0) - Number(r.received || 0);
                  return (
                    <tr key={r.id}>
                      <td>{i + 1}</td><td>{fmtDate(r.receipt_date)}</td>
                      <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>{r.receipt_no}</td>
                      <td style={{ fontWeight: 600 }}>{r.party_name}</td>
                      <td style={{ fontWeight: 600 }}>{r.bank_name || <span style={{ color: '#ccc', fontStyle: 'italic' }}>Unallocated</span>}</td>
                      <td style={{ fontFamily: 'monospace', color: 'var(--secondary)' }}>{r.bill_no}</td>
                      <td className="right-align">₹{fmtNum(r.bill_amt || 0)}</td>
                      <td className="right-align" style={{ color: 'var(--secondary)', fontWeight: 700 }}>₹{fmtNum(r.received)}</td>
                      <td className="right-align" style={{ color: balance > 0 ? 'var(--danger)' : 'var(--secondary)', fontWeight: 700 }}>₹{fmtNum(balance)}</td>
                      <td><span className={`badge ${r.mode === 'Cash' ? 'badge-success' : r.mode === 'UPI' ? 'badge-info' : 'badge-primary'}`}>{r.mode}</span></td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {can('bill_receipts', 'update') &&
                          (isToday(r.receipt_date) || isAdmin) && (
                            <button title="Edit" style={btnEdit} onClick={() => setEditRow({ ...r })}>✏️</button>
                          )}
                        {can('bill_receipts', 'delete') && (
                          <button title="Delete" style={btnDel} onClick={() => setConfirm({ row: r })}>🗑️</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Bill Receipt"
        message={`Delete receipt ${confirm?.row?.receipt_no} from ${confirm?.row?.party_name}? This will restore the customer balance.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
